import { observer } from 'owa-mobx-react';
import MailListSelectAll from './MailListSelectAll';
import MailListSelectAllSLV from './MailListSelectAllSLV';
import { MailListFilterMenu } from 'owa-mail-list-filter-view';
import type ViewFilter from 'owa-service/lib/contract/ViewFilter';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import {
    headerIconContainer,
    headerIconContainerBleedThrough,
    headerIconContainerFull,
    headerIconContainerMediumOrCompact,
} from './MailListHeader.scss';
import { isSingleLineListView } from 'owa-mail-layout';
import { MailListSortMenuV2 } from 'owa-mail-list-sort-view';
import { MailListJumpToPopover, shouldShowJumpTo } from 'owa-mail-list-jumpto-view';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import shouldHideMessageListCheckBoxes from 'owa-messagelist-layout-option/lib/utils/shouldHideMessageListCheckBoxes';
import type { MailFolderTableQuery } from 'owa-mail-list-store';
import {
    isCatchUpFilter,
    isCopilotResultsTable,
    isHeadlinesFilter,
    isPriorityView,
    isNeedsReplyView,
} from 'owa-mail-list-store';
import { PriorityViewHeaderButtons, shouldShowPrioritizeButton } from 'owa-mail-priority-inbox';
import { NeedsReplyViewHeaderButtons } from 'owa-mail-needs-reply';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { isPrioritizeOnDemandOn } from 'owa-copilot-settings-utils';

import classnames from 'owa-classnames';
import type { FunctionComponent } from 'react';

export interface MailListHeaderIconContainerProps {
    tableView: any;
    supportedViewFilters: ViewFilter[];
    filterMenuSource: any;
    tableViewId: string;
    showFullLabels: boolean;
    showSemiCompactLabels: boolean;
    isBleedThrough?: boolean;
}
const defaultExport: FunctionComponent<MailListHeaderIconContainerProps> = observer(
    function MailListHeaderIconContainer(props: MailListHeaderIconContainerProps) {
        const {
            tableView,
            supportedViewFilters,
            filterMenuSource,
            showFullLabels,
            showSemiCompactLabels,
            tableViewId,
            isBleedThrough,
        } = props;

        if (!tableView) {
            return;
        }

        if (
            isCatchUpFilter(tableView.tableQuery as MailFolderTableQuery) ||
            isHeadlinesFilter(tableView.tableQuery as MailFolderTableQuery)
        ) {
            return null;
        }

        const mailboxInfo = getMailboxInfo(tableView);
        const isPrioritizeOnDemandOnValue = isPrioritizeOnDemandOn(mailboxInfo);
        const isCopilotResultsTableSelected = isCopilotResultsTable(tableView.tableQuery);

        const isInNeedsReplyView = isNeedsReplyView(tableView.tableQuery as MailFolderTableQuery);

        const shouldHideHeaderButtons =
            (isPrioritizeOnDemandOnValue &&
                isPriorityView(tableView.tableQuery as MailFolderTableQuery)) ||
            isCopilotResultsTableSelected ||
            isInNeedsReplyView;

        const shouldShowPrioritize =
            shouldShowPrioritizeButton(tableViewId) &&
            filterMenuSource !== 'GroupHeader' &&
            !isInNeedsReplyView;

        return (
            <div
                className={classnames(
                    headerIconContainer,
                    isBleedThrough && headerIconContainerBleedThrough,
                    getDensityModeCssClass(
                        headerIconContainerFull,
                        headerIconContainerMediumOrCompact,
                        headerIconContainerMediumOrCompact
                    )
                )}
            >
                {!isGlobalFeatureEnabled('tri-reselect-checkbox-behavior') &&
                    !shouldHideMessageListCheckBoxes() &&
                    (isSingleLineListView() ? (
                        <MailListSelectAllSLV
                            tableView={tableView}
                            showButtonLabel={showFullLabels}
                        />
                    ) : (
                        <MailListSelectAll tableView={tableView} />
                    ))}
                {filterMenuSource !== 'GroupHeader' &&
                    !shouldHideHeaderButtons &&
                    shouldShowJumpTo(tableView) && (
                        <MailListJumpToPopover
                            tableViewId={tableViewId}
                            showButtonLabel={showFullLabels}
                            isBleedThrough={isBleedThrough}
                        />
                    )}
                {filterMenuSource !== 'GroupHeader' &&
                    !isCopilotResultsTableSelected &&
                    !isInNeedsReplyView && (
                        <MailListFilterMenu
                            filterMenuSource={filterMenuSource}
                            supportedViewFilters={supportedViewFilters}
                            showButtonLabel={showFullLabels}
                            tableViewId={tableViewId}
                            isBleedThrough={isBleedThrough}
                        />
                    )}
                {filterMenuSource !== 'GroupHeader' && !shouldHideHeaderButtons && (
                    <MailListSortMenuV2
                        sortMenuSource={filterMenuSource}
                        tableViewId={tableViewId}
                        showButtonLabel={showFullLabels || showSemiCompactLabels}
                        isBleedThrough={isBleedThrough}
                    />
                )}
                {shouldShowPrioritize && (
                    <PriorityViewHeaderButtons
                        tableViewId={tableViewId}
                        isBleedThrough={isBleedThrough ?? false}
                        showButtonLabel={showFullLabels}
                    />
                )}
                {isInNeedsReplyView && (
                    <NeedsReplyViewHeaderButtons
                        isBleedThrough={isBleedThrough ?? false}
                        showButtonLabel={showFullLabels}
                    />
                )}
            </div>
        );
    },
    'MailListHeaderIconContainer'
);
export default defaultExport;
